import { CdkDrag } from "@angular/cdk/drag-drop";
import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { MatTable } from "@angular/material/table";
import { GlobalHelpDialogComponent } from "@comunicare/comunicare-dashboard-modals";
import { DataType, FHIRHelper, IActivityLog, IFilter, IPatient, WidgetName } from "@comunicare/comunicare-interfaces";
import * as moment from "moment";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { Base64Helper } from "src/app/helpers/Base64Helper";
import { HelpData } from "src/app/helpers/helpData";
import { IWidgetComponent } from "src/app/models/widget.interface";
import { ActivityService } from "src/app/providers/activity.service";
import { PatientService } from "src/app/providers/patient.service";
import { ResponsiveDialogService } from "src/app/providers/responsive-dialog.service";
import { LastPatientListDataSource } from "./last-patient-widget-list-datasource";

@Component({
  selector: "app-last-patient-widget",
  templateUrl: "./last-patient-widget.component.html",
  styleUrls: ["./last-patient-widget.component.scss"],
})
export class LastPatientWidgetComponent implements IWidgetComponent, OnInit, OnDestroy {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatTable) table: MatTable<IActivityLog>;
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(CdkDrag, { static: true }) cdkDrag: CdkDrag;

  public widgetName = WidgetName;
  public dataTypeText = DataType.TEXT;
  public dataTypeDate = DataType.DATE;
  /** Lower bound for date filters: users cannot filter further back than one year ago. */
  public minFilterDate = moment().subtract(1, "year");
  public isDraggable = true;
  public displayedColumns: string[] = ["patient", "date"];
  public dataSource: LastPatientListDataSource;
  /** Raw active filters, kept in sync to feed `app-item-filter` via the `filterFor` pipe. */
  public activeFilters: IFilter[] = [];
  public activities: IActivityLog[] = [];
  /** Subject that emits when the component has been destroyed. */
  // eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle, id-denylist, id-match
  private onDestroy$ = new Subject<void>();

  constructor(
    private activityService: ActivityService,
    private patientService: PatientService,
    private responsiveDialog: ResponsiveDialogService,
    private helpData: HelpData
  ) {}

  ngOnInit(): void {
    this.dataSource = new LastPatientListDataSource(this.patientService);
    this.initData();
  }

  ngOnDestroy(): void {
    this.dataSource.clear();
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  private initData() {
    this.activityService
      .list()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((activities) => {
        this.activities = activities;
        this.loadDataSource();
      });
  }

  private loadDataSource() {
    this.dataSource.loadData(this.activities);
    this.initPaginatorAndSort();
  }

  private initPaginatorAndSort() {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
    this.table.dataSource = this.dataSource;
  }

  public getCaremateIdentifier(patient: IPatient): string {
    return FHIRHelper.getMainIdentifier(patient).value;
  }

  public encodeToB64(str: string): string {
    return Base64Helper.utf8_to_b64(str);
  }

  public openLastPatientsHelp(): void {
    this.responsiveDialog.open(
      GlobalHelpDialogComponent,
      {
        data: { slides: this.helpData.lastPatients },
        disableClose: true,
      },
      { maxWidth: "80vw" }
    );
  }

  /**
   * IFilter
   */
  public getFilter(propertyName: string): IFilter {
    return this.dataSource.getFilter(propertyName);
  }

  public applyFilter(filter: IFilter): void {
    this.dataSource.setFilter(filter);
    this.activeFilters = [...(this.dataSource?.getAllFilters() ?? [])];
  }

  public clearFilter(): void {
    this.dataSource.clearFilter();
    this.activeFilters = [];
  }
}
